import React, { useCallback, useContext, useMemo } from 'react';
import moment from 'moment';
import { isMobileOnly } from 'react-device-detect';
import { useRecoilValue } from 'recoil';
import { selectedBranches } from '@ubeya/shared/atoms/shared';
import useTags from '@ubeya/shared/hooks/account/useTags';
import useAdmins from '@ubeya/shared/hooks/account/useAdmins';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useBranches from '@ubeya/shared/hooks/account/useBranches';
import useJobTypes from '@ubeya/shared/hooks/generic/useJobTypes';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useTimesheetFieldsColumns from '@ubeya/shared-web/hooks/useTimesheetFieldsColumns';
import useSuppliers from '@ubeya/shared/hooks/account/useSuppliers';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import {
  SORT_BY_BOOLEAN,
  SORT_BY_BREAKS_DURATION,
  SORT_BY_DATE,
  SORT_BY_DATE_AND_TIME,
  SORT_BY_NAME,
  SORT_BY_NUMBER,
  SORT_BY_STRING,
  SORT_BY_TOTAL
} from '@ubeya/shared/utils/sorting';
import {
  API_DATE_FORMAT,
  API_DATE_TIME_FORMAT,
  TIMESHEET_GROUP_BY_OPTIONS_ENUM,
  SLOT_FIELDS
} from '@ubeya/shared/constants';
import { TIMESHEET_TABLE_CONTEXT } from 'shared/constants/timeSheet';
import { DISABLE_FILL_HOURS_BY_JOB_TYPE } from 'timeSheet/constants';
import {
  ApproveAggregateRenderer,
  ApproveRenderer,
  AreaRenderer,
  BreaksRenderer,
  CheckInTimeRenderer,
  CheckOutTimeRenderer,
  DateRenderer,
  DetailsRenderer,
  InTimeRenderer,
  LockedTimesheetRenderer,
  NameAggregateRenderer,
  NameRenderer,
  OutTimeRenderer,
  PositionRenderer,
  ShiftAggregateRenderer,
  ShiftRenderer,
  TotalTimeRenderer,
  TotalTimeWithoutBreaksRenderer
} from 'timeSheet/components/CellRenderers';
import DifferenceRenderer from 'timeSheet/components/CellRenderers/DifferenceRenderer';
import TagsRenderer from 'timeSheet/components/CellRenderers/TagsRenderer';
import AdminApprovalRenderer from 'timeSheet/components/CellRenderers/AdminApprovalRenderer';
import BranchRenderer from 'timeSheet/components/CellRenderers/BranchRenderer';
import PayrollProcessAggregatorRenderer from 'timeSheet/components/CellRenderers/PayrollProcessAggregatorRenderer';
import PayrollProcessRenderer from 'timeSheet/components/CellRenderers/PayrollProcessRenderer';
import ReviewOnEmployeeRenderer from 'timeSheet/components/CellRenderers/ReviewOnEmployeeRenderer';
import ExpandedRatingRenderer from 'timeSheet/components/ExpandedRatingRenderer';
import { ClientTimesheetResponseStatus } from 'payroll/components/CellRenderers';
import EmployeeTimesheetResponseStatus from 'payroll/components/CellRenderers/EmployeeTimesheetResponseStatus';
import { generateTimesheetSortingMap } from 'timeSheet/utils';

/**
 * Legacy table column definitions for PDF export from TimeSheetTableGrid.
 * Must be kept in sync with TimeSheetTable (source of truth). Used only by TimeSheetTableGrid for TimeSheetPDF.
 */
export const useTimesheetLegacyTableColumns = ({
  approveTimeSheet,
  updateOSClientApproval,
  setShiftModal,
  setSelectedEmployee,
  showLockedTimesheetColumn,
  showPayrollProcessFeature,
  showEmployeeSignoffColumn,
  showDateColumn,
  tableContext,
  groupBy,
  showEmployeeRating,
  updateStartTime,
  updateOSStartTime,
  updateEndTime,
  updateOSEndTime,
  hasActionPermission,
  setIsPromptOpen,
  showCheckInOutColumns,
  updateCheckInTime,
  updateOSCheckInTime,
  updateCheckOutTime,
  updateOSCheckOutTime,
  updateBreak,
  deleteBreak,
  updateOSBreaks,
  deleteOSBreak,
  updateEmployeeRating,
  updateOSEmployeeRating,
  updateReviewOnEmployee,
  updateOSReviewOnEmployee,
  refetchTimesheet,
  showShiftColumn,
  showTimesheetFields,
  clickableDate,
  isBreakTimesDisplayed
}) => {
  const { t, timeFormat } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { mappedSupplierBranches } = useSuppliers();
  const { mappedBranches, branchesOptions } = useBranches();
  const { mappedPositions } = usePositions();
  const { mappedLocations } = useLocations();
  const { mappedLabels } = useLabels();
  const { mappedJobTypes } = useJobTypes();
  const { mappedTags } = useTags();
  const { mappedAdmins } = useAdmins();
  const selectedBranchesValue = useRecoilValue(selectedBranches);
  const showBranchColumn = (branchesOptions || []).every(({ value }) => (selectedBranchesValue || []).includes(value));
  const timesheetFieldsColumns = useTimesheetFieldsColumns({ showTimesheetFields });

  const captureRowClick = useCallback(
    ({ rowData: { subRows, jobTypeId } }) => !!subRows || DISABLE_FILL_HOURS_BY_JOB_TYPE[jobTypeId],
    []
  );

  const timesheetSortingFunctionsMap = useMemo(
    () =>
      generateTimesheetSortingMap({
        mappedJobTypes,
        mappedLabels,
        mappedLocations,
        mappedBranches,
        mappedPositions,
        t
      }),
    [mappedBranches, mappedJobTypes, mappedLabels, mappedLocations, mappedPositions, t]
  );

  return useMemo(
    () => [
      {
        Header: t('columnHeaderApprovedPayroll'),
        dataKey: 'approve',
        Aggregate: tableContext === TIMESHEET_TABLE_CONTEXT.EMPLOYEE_JOB_TAB ? null : ApproveAggregateRenderer,
        Cell: ApproveRenderer,
        sortFunc: SORT_BY_BOOLEAN('approved'),
        columnData: {
          approveTimeSheet,
          editOSTimeSheet: updateOSClientApproval,
          mappedSupplierBranches
        },
        frozen: true,
        minWidth: 80,
        maxWidth: 80,
        resizable: false,
        captureRowClick: false,
        className: 'grid-cell centered'
      },
      showLockedTimesheetColumn && {
        Header: t('timesheetLocked'),
        dataKey: 'isLocked',
        Cell: LockedTimesheetRenderer,
        sortFunc: SORT_BY_BOOLEAN('isLocked'),
        frozen: true,
        minWidth: 80,
        maxWidth: 80,
        resizable: false,
        captureRowClick: false,
        category: t('approval'),
        cellRendererProps: { style: { margin: 'auto' } }
      },
      showPayrollProcessFeature && {
        Header: t('columnHeaderProcessedPayroll'),
        dataKey: 'process',
        frozen: true,
        Aggregate: PayrollProcessAggregatorRenderer,
        Cell: PayrollProcessRenderer,
        maxWidth: 85,
        minWidth: 85,
        category: t('approval'),
        visible: true,
        cellRendererProps: { style: { margin: 'auto' } }
      },
      {
        Header: t('approvedBy'),
        dataKey: 'adminApproved.adminId',
        Cell: AdminApprovalRenderer,
        columnData: { mappedAdmins },
        width: 100,
        minWidth: 100,
        sortable: false,
        visible: false,
        captureRowClick: false,
        category: t('approval')
      },
      {
        Header: t('approvedAt'),
        dataKey: 'adminApproved.createdAt',
        formatter: (value, { isExternal, clientApproved }) =>
          value
            ? moment(value).format(API_DATE_TIME_FORMAT)
            : clientApproved && isExternal
            ? moment(clientApproved.createdAt).format(API_DATE_TIME_FORMAT)
            : '',
        width: 80,
        minWidth: 50,
        visible: false,
        sortable: false,
        captureRowClick: false,
        category: t('approval')
      },
      {
        Header: t('clientStatus'),
        dataKey: 'clientApproved',
        Cell: ClientTimesheetResponseStatus,
        maxWidth: 120,
        minWidth: 120,
        captureRowClick,
        category: t('approval'),
        sortable: false,
        visible: false,
        cellRendererProps: { style: { height: '100%', width: '100%' } },
        cellWrapperProps: { style: { padding: '2px' } }
      },
      showEmployeeSignoffColumn && {
        Header: t('employeeStatus'),
        dataKey: 'employeeApproved',
        Cell: EmployeeTimesheetResponseStatus,
        maxWidth: 130,
        minWidth: 130,
        captureRowClick,
        category: t('approval'),
        sortable: false,
        visible: false,
        cellRendererProps: { style: { height: '100%', width: '100%' } },
        cellWrapperProps: { style: { padding: '2px' } }
      },
      showDateColumn && {
        Header: t('shiftDate'),
        dataKey: 'scheduleIn',
        formatter: (value) => <>{value ? moment(value).format(API_DATE_FORMAT) : ''}</>,
        captureRowClick,
        sortFunc: SORT_BY_DATE_AND_TIME('scheduleIn'),
        category: t('basic')
      },
      tableContext === TIMESHEET_TABLE_CONTEXT.EMPLOYEE_JOB_TAB
        ? {
            Header: t('Date'),
            dataKey: 'name',
            Cell: DateRenderer,
            flexGrow: isMobileOnly ? 1 : 1.1,
            minWidth: isMobileOnly ? 100 : 140,
            maxWidth: 140,
            frozen: true,
            sortFunc: SORT_BY_DATE('date'),
            columnData: { accountId, clickableDate }
          }
        : {
            Header: t('name'),
            dataKey: 'name',
            Aggregate: NameAggregateRenderer,
            Cell: NameRenderer,
            columnData: {
              groupBy,
              accountId,
              clickableDate,
              showEmployeeRating,
              onClickEmployee: setSelectedEmployee
            },
            flexGrow: isMobileOnly ? 1 : 1.1,
            minWidth: isMobileOnly ? 100 : 300,
            frozen: true,
            sortFunc: groupBy === TIMESHEET_GROUP_BY_OPTIONS_ENUM.EMPLOYEE ? SORT_BY_DATE('date') : SORT_BY_NAME,
            cellRendererProps: {
              style: {
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                position: 'relative'
              },
              enabled: false
            }
          },
      {
        Header: t('startTimes'),
        dataKey: 'scheduleIn',
        formatter: (value) => <>{value ? moment(value).format(timeFormat) : ''}</>,
        captureRowClick,
        sortFunc: SORT_BY_DATE_AND_TIME('scheduleIn'),
        category: t('scheduling')
      },
      {
        Header: t('endTimes'),
        dataKey: 'scheduleOut',
        formatter: (value) => <>{value ? moment(value).format(timeFormat) : ''}</>,
        captureRowClick,
        sortFunc: SORT_BY_DATE_AND_TIME('scheduleOut'),
        category: t('scheduling')
      },
      [
        TIMESHEET_GROUP_BY_OPTIONS_ENUM.POSITION,
        TIMESHEET_GROUP_BY_OPTIONS_ENUM.PROJECT,
        TIMESHEET_GROUP_BY_OPTIONS_ENUM.CLIENT
      ].includes(groupBy)
        ? {
            Header: t('date'),
            dataKey: 'date',
            Cell: DateRenderer,
            columnData: { groupBy },
            sortFunc: SORT_BY_DATE('date'),
            category: t('basic')
          }
        : undefined,
      {
        Header: t('in'),
        dataKey: 'in',
        Cell: InTimeRenderer,
        columnData: {
          updateStartTime,
          updateOSStartTime,
          updateEndTime,
          updateOSEndTime,
          readOnly: !hasActionPermission,
          allowUpdateExternal: true,
          onOutsideClickSave: true,
          showClientTimesheetOriginalHours: true,
          timeFormat,
          setIsPromptOpen
        },
        minwidth: 100,
        maxWidth: 115,
        captureRowClick,
        sortFunc: SORT_BY_DATE_AND_TIME('actualIn'),
        category: t('scheduling')
      },
      ...(showCheckInOutColumns
        ? [
            {
              Header: t('checkInTime'),
              dataKey: 'checkIn',
              Cell: CheckInTimeRenderer,
              columnData: {
                updateCheckInTime,
                updateOSCheckInTime,
                updateCheckOutTime,
                updateOSCheckOutTime,
                readOnly: !hasActionPermission,
                allowUpdateExternal: true,
                onOutsideClickSave: true
              },
              minwidth: 100,
              maxWidth: 115,
              captureRowClick,
              sortFunc: SORT_BY_DATE_AND_TIME('actualCheckIn'),
              category: t('scheduling')
            },
            {
              Header: t('checkOutTime'),
              dataKey: 'checkOut',
              Cell: CheckOutTimeRenderer,
              columnData: {
                updateCheckOutTime,
                updateOSCheckOutTime,
                readOnly: !hasActionPermission,
                allowUpdateExternal: true,
                onOutsideClickSave: true
              },
              minwidth: 100,
              maxWidth: 115,
              captureRowClick,
              sortFunc: SORT_BY_DATE_AND_TIME('actualCheckOut'),
              category: t('scheduling')
            }
          ]
        : []),
      {
        Header: t('out'),
        dataKey: 'out',
        Cell: OutTimeRenderer,
        columnData: {
          updateEndTime,
          updateOSEndTime,
          readOnly: !hasActionPermission,
          allowUpdateExternal: true,
          onOutsideClickSave: true,
          showClientTimesheetOriginalHours: true,
          timeFormat,
          setIsPromptOpen
        },
        minwidth: 100,
        maxWidth: 115,
        captureRowClick,
        sortFunc: SORT_BY_DATE_AND_TIME('actualOut'),
        category: t('scheduling')
      },
      {
        Header: t('totalTime'),
        dataKey: 'total',
        maxWidth: 90,
        Cell: TotalTimeRenderer,
        columnData: {
          updateStartTime,
          updateEndTime,
          updateOSStartTime,
          updateOSEndTime,
          readOnly: !hasActionPermission
        },
        sortFunc: SORT_BY_TOTAL,
        category: t('scheduling')
      },
      {
        Header: t('totalTimeWithoutBreaks'),
        dataKey: 'totalWithoutBreaks',
        Cell: TotalTimeWithoutBreaksRenderer,
        sortFunc: SORT_BY_TOTAL,
        maxWidth: 90,
        category: t('scheduling')
      },
      groupBy !== TIMESHEET_GROUP_BY_OPTIONS_ENUM.POSITION
        ? {
            Header: t('position'),
            dataKey: 'position',
            Cell: PositionRenderer,
            columnData: { mappedPositions },
            sortFunc: timesheetSortingFunctionsMap.sortByPosition,
            category: t('basic')
          }
        : undefined,
      showShiftColumn && {
        Header: t('shift'),
        dataKey: 'shift',
        Aggregate: tableContext === TIMESHEET_TABLE_CONTEXT.EMPLOYEE_JOB_TAB ? null : ShiftAggregateRenderer,
        Cell: ShiftRenderer,
        sortFunc: timesheetSortingFunctionsMap.sortByShiftLabel,
        columnData: { accountId, mappedLabels, mappedJobTypes, mappedLocations },
        cellRendererProps: { style: { width: '100%' } },
        category: t('basic')
      },
      showBranchColumn && {
        Header: t('branch'),
        dataKey: 'branchId',
        Cell: BranchRenderer,
        columnData: { mappedBranches },
        sortFunc: timesheetSortingFunctionsMap.sortByBranchName,
        category: t('basic')
      },
      {
        Header: t('shiftArea'),
        dataKey: 'locationId',
        Cell: AreaRenderer,
        columnData: { mappedLocations },
        flexGrow: 2.5,
        sortFunc: timesheetSortingFunctionsMap.sortByAreaName,
        category: t('basic')
      },
      {
        Header: t('costCenter'),
        dataKey: 'costCenter',
        formatter: (costCenterValue) =>
          costCenterValue
            ? `${costCenterValue.name || ''}${costCenterValue.identifier ? ` (${costCenterValue.identifier})` : ''}`
            : '',
        minWidth: 100,
        maxWidth: 150,
        sortFunc: SORT_BY_STRING('costCenter.name'),
        category: t('basic')
      },
      {
        Header: t('client'),
        dataKey: 'clientName',
        formatter: (value) => value,
        flexGrow: 2.5,
        visible: false,
        sortable: false,
        category: t('basic')
      },
      {
        Header: t('tags'),
        dataKey: 'tags',
        Cell: TagsRenderer,
        columnData: { mappedTags },
        visible: false,
        category: t('basic')
      },
      {
        Header: t('difference'),
        dataKey: 'difference',
        maxWidth: 100,
        Cell: DifferenceRenderer,
        sortFunc: SORT_BY_NUMBER('difference'),
        visible: false,
        category: t('scheduling')
      },
      isBreakTimesDisplayed && {
        Header: t('breaks'),
        dataKey: 'breaks',
        maxWidth: 100,
        Cell: BreaksRenderer,
        columnData: { updateBreak, deleteBreak, readOnly: !hasActionPermission, updateOSBreaks, deleteOSBreak },
        sortable: true,
        sortFunc: SORT_BY_BREAKS_DURATION('breaktimes'),
        category: t('scheduling')
      },
      {
        Header: t('rating'),
        dataKey: 'rating',
        Cell: ExpandedRatingRenderer,
        columnData: {
          onChangeRating: updateEmployeeRating,
          onExternalChangeRating: updateOSEmployeeRating,
          onEmployeeStatusChange: refetchTimesheet,
          mappedSupplierBranches
        },
        sortFunc: SORT_BY_NUMBER('rating'),
        minWidth: 175,
        maxWidth: 185,
        visible: true,
        category: t('basic')
      },
      {
        Header: t('reviewOnEmployee'),
        dataKey: 'review',
        minWidth: 180,
        formatter: (value) => value,
        Cell: ReviewOnEmployeeRenderer,
        columnData: {
          onUpdate: updateReviewOnEmployee,
          onExternalUpdate: updateOSReviewOnEmployee,
          key: SLOT_FIELDS.reviewOnEmployee,
          enablePreviewModal: true,
          showEditIcon: true,
          isSingleRow: true,
          readOnly: !updateReviewOnEmployee && !updateOSReviewOnEmployee
        },
        readOnly: false,
        flexGrow: 1,
        visible: false,
        sortable: false,
        category: t('basic'),
        cellRendererProps: {
          style: { width: '100%' },
          enabled: false
        }
      },
      {
        Header: t('details'),
        dataKey: 'details',
        Cell: DetailsRenderer,
        columnData: { onShiftClick: setShiftModal },
        flexGrow: 2.5,
        visible: false,
        sortable: false,
        category: t('basic')
      },
      ...timesheetFieldsColumns
    ],
    [
      t,
      timeFormat,
      tableContext,
      updateReviewOnEmployee,
      approveTimeSheet,
      updateOSClientApproval,
      mappedSupplierBranches,
      showLockedTimesheetColumn,
      showPayrollProcessFeature,
      mappedAdmins,
      captureRowClick,
      showEmployeeSignoffColumn,
      showDateColumn,
      accountId,
      clickableDate,
      groupBy,
      showEmployeeRating,
      setSelectedEmployee,
      updateStartTime,
      updateOSStartTime,
      updateEndTime,
      updateOSEndTime,
      hasActionPermission,
      setIsPromptOpen,
      showCheckInOutColumns,
      updateCheckInTime,
      updateOSCheckInTime,
      updateCheckOutTime,
      updateOSCheckOutTime,
      mappedPositions,
      timesheetSortingFunctionsMap,
      showShiftColumn,
      showBranchColumn,
      mappedBranches,
      mappedLocations,
      mappedLabels,
      mappedJobTypes,
      mappedTags,
      isBreakTimesDisplayed,
      updateBreak,
      deleteBreak,
      updateOSBreaks,
      deleteOSBreak,
      updateEmployeeRating,
      updateOSEmployeeRating,
      updateOSReviewOnEmployee,
      refetchTimesheet,
      setShiftModal,
      timesheetFieldsColumns
    ]
  );
};
